/**
 * ws-charger-spin.js — scroll-driven turntable for the best-seller charger.
 *
 * The static hero image is replaced, frame by frame, with renders of the real
 * ZQ8 model turning through 120 degrees while it grows from small to full size.
 * Every frame was composited onto the same 900x600 canvas as the original photo
 * and anchored on the charger's white shell, so swapping src never moves or
 * resizes the product — the existing `.ws-bs-cover-img` rules (52% width on
 * desktop, object-fit: cover on mobile) keep working untouched.
 *
 * The sequence is built so the model comes to rest exactly as it reaches the
 * middle of the viewport: the turn and the zoom share one clock and both finish
 * there, and only then do the two callout pins animate in. The pins never turn
 * or scale with the model.
 *
 * Failure is always a no-op: until frames start arriving the element keeps
 * showing the original image, the pins are only ever hidden by this script, and
 * if nothing loads everything stays exactly as it was.
 */
(function () {
  'use strict';

  var ATTR = 'data-ws-spin';

  function boot(img) {
    if (img.dataset.wsSpinBound) return;
    img.dataset.wsSpinBound = '1';

    var urls;
    try { urls = JSON.parse(img.getAttribute(ATTR)); } catch (e) { return; }
    if (!urls || urls.length < 2) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var stage    = img.closest('.ws-combined-stage') || img.parentElement;
    var group    = img.closest('.ws-bs-image-group');
    var pinLayer = (group || stage).querySelector('.ws-bs-callout-img-layer');

    var frames    = [];        // Image objects, in order
    var loaded    = [];        // parallel flags — we can draw before all arrive
    var anyReady  = false;
    var current   = -1;
    var ticking   = false;
    var lastScale = -1;
    var pinsShown = null;

    // How small it starts.
    var ZOOM_MIN = parseFloat(img.getAttribute('data-ws-spin-zoom')) || 0.45;
    // Past this point the model is parked; that is when the pins fly in.
    var SETTLED  = 0.995;
    // While pinned, the turn and zoom run over the first slice of the travel and
    // the rest is a deliberate hold: the finished model, with its pins in, sits
    // still before the section releases and scrolls away. The split is computed
    // from two independent scroll distances in measure(), not a fixed fraction,
    // so lengthening the hold never slows the turn down.
    var animEnd = 0.86;

    var wrap   = img.closest('.ws-combined-stage-wrap');
    var runway = null;
    var stickyTop = 0;
    var travel = 0;

    // ── Frame sizing ──────────────────────────────────────────────────────
    // Shopify re-encodes to WebP and resizes on request, so ask for roughly
    // what the element actually occupies rather than always pulling the full
    // 900px plate. A phone rendering the image ~390px wide does not need the
    // same bytes as a 978px desktop stage, and across 48 frames that is the
    // difference between a few hundred KB and most of a megabyte. Capped at
    // 1.5x rather than full DPR: this is a thing in motion, and the extra
    // sharpness is not worth doubling every frame.
    function frameWidth() {
      var css  = img.getBoundingClientRect().width || img.clientWidth || 600;
      var want = css * Math.min(window.devicePixelRatio || 1, 1.5);
      if (want <= 460) return 460;
      if (want <= 640) return 640;
      return 900;
    }

    function url(base, w) {
      return base + (base.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
    }

    // ── Scroll pinning ────────────────────────────────────────────────────
    // Build the runway around the stage wrapper at runtime. Doing it here rather
    // than in the Liquid means the markup on disk is unchanged and a failure to
    // load this file leaves the section laying out exactly as before.
    function buildRunway() {
      if (!wrap || !wrap.parentNode) return;
      runway = document.createElement('div');
      runway.className = 'ws-spin-runway';
      wrap.parentNode.insertBefore(runway, wrap);
      runway.appendChild(wrap);
    }

    // Ask the wrapper, not the runway: `display: contents` (which is what the
    // mobile breakpoint does to these wrappers) leaves an element's computed
    // `position` untouched, so testing the runway would report pinning as live
    // on phones, where the sticky rule is not applied at all.
    function pinning() {
      return !!runway && !!wrap && getComputedStyle(wrap).position === 'sticky';
    }

    // Scroll distance the turn and zoom occupy while pinned. open-design.ai holds
    // theirs for 1256px across four states; this is the same order.
    function animFor(vh) {
      var declared = parseFloat(img.getAttribute('data-ws-spin-anim'));
      if (declared > 0) return declared;
      return Math.min(860, Math.max(520, vh * 0.86));
    }

    // Scroll distance the finished model is held before the section lets go.
    // Its own distance rather than whatever fraction of the travel is left over,
    // because those are different jobs: otherwise the end pose flicks past in
    // about one wheel notch and nobody gets to look at the thing the whole
    // section was building up to.
    function holdFor(vh) {
      var declared = parseFloat(img.getAttribute('data-ws-spin-hold'));
      if (declared >= 0) return declared;
      return Math.min(620, Math.max(380, vh * 0.7));
    }

    // Choose the pin offset so the charger itself ends up centred in the space
    // below the nav — the wrapper's own box is not a useful guide, because the
    // image is positioned 139px ABOVE the wrapper's top edge.
    function measure() {
      if (!pinning()) {
        if (runway) runway.style.height = '';
        if (wrap) wrap.style.top = '';
        return;
      }
      var vh = window.innerHeight || document.documentElement.clientHeight;

      // Measure unzoomed: the zoom transform changes the image's rect, and the
      // pin offset must not depend on where in the animation we happen to be.
      var prev = img.style.getPropertyValue('--ws-spin-zoom');
      img.style.setProperty('--ws-spin-zoom', '1');
      var wr = wrap.getBoundingClientRect();
      var ir = img.getBoundingClientRect();
      var imgOffset = ir.top - wr.top;       // negative: image sits above the wrapper
      var imgH = ir.height;
      if (prev) img.style.setProperty('--ws-spin-zoom', prev);
      else img.style.removeProperty('--ws-spin-zoom');

      var NAV = 64;
      var top = NAV + ((vh - NAV) - imgH) / 2 - imgOffset;
      // On a tall window the centring maths would push the pin so far down that
      // the price and CTA below the charger fall off the bottom; cap it.
      stickyTop = Math.max(0, Math.round(Math.min(top, vh * 0.35)));
      var animPx = Math.round(animFor(vh));
      var holdPx = Math.round(holdFor(vh));
      travel = animPx + holdPx;
      animEnd = animPx / travel;

      wrap.style.top = stickyTop + 'px';
      runway.style.height = (wrap.offsetHeight + travel) + 'px';
    }

    // ── Scroll mapping ────────────────────────────────────────────────────
    // Distance over which the whole thing plays out, ending at centre. Short on
    // purpose: mapping it over the section's full pass through the viewport
    // meant most of the turn happened while the charger was half off-screen,
    // so it looked like it barely moved.
    function span() {
      var declared = parseFloat(img.getAttribute('data-ws-spin-span'));
      if (declared > 0) return declared;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return Math.min(700, Math.max(360, vh * 0.6));
    }

    // While the section is pinned, progress is simply how far through the runway
    // we are: 0 as the panel locks into place, 1 as it is about to let go. The
    // panel is motionless throughout, so the only thing that moves is the model.
    //
    // Where pinning is not in play (mobile, where the stage wrappers collapse to
    // display:contents), fall back to driving it off the image's own travel
    // through the viewport, finishing as it reaches the middle.
    function progress() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var t;
      if (pinning() && travel > 0) {
        t = (stickyTop - runway.getBoundingClientRect().top) / travel;
      } else {
        var r = img.getBoundingClientRect();
        var below = (r.top + r.height / 2) - vh / 2;
        t = 1 - below / span();
      }
      return t < 0 ? 0 : t > 1 ? 1 : t;
    }

    // The animation occupies the first stretch of that progress; the tail is the
    // hold. Off the pinned path there is nothing to hold, so it uses all of it.
    function animProgress() {
      var t = progress();
      if (!pinning()) return t;
      var a = t / animEnd;
      return a > 1 ? 1 : a;
    }

    // Frames arrive over the network in no guaranteed order, and we start
    // animating on the first one rather than waiting for all 48. Snapping to
    // the closest frame that has actually decoded means the turn is coarse for
    // a moment and then fills in, instead of stalling on the static photo.
    function nearestLoaded(i) {
      if (loaded[i]) return i;
      for (var d = 1; d < frames.length; d++) {
        if (loaded[i - d]) return i - d;
        if (loaded[i + d]) return i + d;
      }
      return -1;
    }

    function draw() {
      ticking = false;
      if (!anyReady) return;
      var t = animProgress();

      var i = nearestLoaded(Math.round(t * (frames.length - 1)));
      if (i >= 0 && i !== current) {
        current = i;
        img.src = frames[i].src;
      }

      // Zoom shares the clock with the turn.
      var z = 1 - Math.pow(1 - t, 3);              // ease out: quick, then settles
      var s = ZOOM_MIN + (1 - ZOOM_MIN) * z;
      if (Math.abs(s - lastScale) > 0.002) {
        lastScale = s;
        // Composed into the section's own transform rules through a custom
        // property. Setting `transform` here would be ignored (those rules are
        // !important), and setting the `scale` property would scale the
        // centring translate along with it, dragging the product off-centre.
        img.style.setProperty('--ws-spin-zoom', s.toFixed(3));
      }

      if (pinLayer) {
        var show = t >= SETTLED;
        if (show !== pinsShown) {
          pinsShown = show;
          pinLayer.classList.toggle('ws-spin-pins-in', show);
        }
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(draw);
    }

    function preload() {
      var w = frameWidth();
      urls.forEach(function (base, i) {
        var im = new Image();
        im.decoding = 'async';
        im.onload = function () {
          loaded[i] = true;
          if (!anyReady) {
            anyReady = true;
            // srcset would otherwise override every src we set from here on
            // and the frame would never change.
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
            img.style.willChange = 'transform';
            if (pinLayer) pinLayer.classList.add('ws-spin-pins-armed');
          }
          draw();
        };
        im.onerror = function () { loaded[i] = false; };
        im.src = url(base, w);
        frames[i] = im;
      });
    }

    // Only pay for the frames once the section is actually approaching.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        preload();
      }, { rootMargin: '600px 0px' });
      io.observe(stage);
    } else {
      preload();
    }

    buildRunway();
    measure();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { measure(); onScroll(); }, { passive: true });
    // Fonts and late images change the wrapper's height underneath us.
    window.addEventListener('load', function () { measure(); onScroll(); });
  }

  function scan() {
    var nodes = document.querySelectorAll('[' + ATTR + ']');
    Array.prototype.forEach.call(nodes, boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();
